import { MIME_TYPE_JSON_LD } from "../constants/mimeTypes";
import { createAffiliateError, createFetchError } from "./errors";
import { getPaginationProperties } from "../helpers/api/pagination";
import { getConfigValue } from "@rumble-it/common-lib/src/config";
import { User } from "oidc-client-ts";

/**
 * Gets the affiliate ID from the session token.
 */
export const getAffiliateId = async (
  session: any & { affiliateIds: string[] },
) => {
  if (!session.affiliateIds) {
    throw createAffiliateError("User has no affiliates (key not found)");
  }

  if (session.affiliateIds.length === 0) {
    throw createAffiliateError("User has no affiliates");
  }

  if (session.affiliateIds.length > 1) {
    const err = createAffiliateError(
      "User has multiple affiliates, not supported yet",
    );
    // TODO Remove this logic once multi-tenant support is added
    if (getConfigValue("mode") === "development") {
      console.error(err);
    } else {
      throw err;
    }
  }

  return session.affiliateIds.sort()[0];
};

/**
 * Fetches data from the API.
 */
type FetchEndpointOptions = {
  endpoint: string;
  pagination?: { page?: number; itemsPerPage?: number };
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: any;
  redirectTo?: string | null;
  includeAffiliatePathPrefix?: boolean;
  contentType?: string;
  accept?: string;
  queryParams?: any;
};
export async function fetchEndpoint({
  endpoint,
  pagination,
  method = "GET",
  data = null,
  redirectTo = null,
  includeAffiliatePathPrefix = true,
  contentType = MIME_TYPE_JSON_LD,
  accept = MIME_TYPE_JSON_LD,
  queryParams = {},
}: FetchEndpointOptions) {
  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${getConfigValue("authUrl")}/realms/${getConfigValue("authRealm")}:${getConfigValue("authClientId")}`,
  );

  if (!oidcStorage) {
    throw new Error("OIDC user not found in session storage.");
  }

  const user = User.fromStorageString(oidcStorage);

  const affiliateStorage = sessionStorage.getItem(
    `api.user.affiliate:${user?.profile?.sub}`,
  );

  if (!affiliateStorage) {
    throw new Error("Affiliate ID not found in session storage.");
  }

  const affiliateId = affiliateStorage;

  const accessToken = user?.access_token || null;

  const url =
    `${getConfigValue("apiUrl")}` +
    `${affiliateId && includeAffiliatePathPrefix === true ? `/affiliates/${affiliateId}` : ""}` +
    `${endpoint.startsWith("/") ? endpoint : "/" + endpoint}`;

  const headers = {
    Accept: accept,
    "Content-Type": contentType,
    Authorization: `Bearer ${accessToken}`,
  };

  const body = data ? JSON.stringify(data) : undefined;

  const paginationParams = pagination
    ? {
        ...(pagination.page !== undefined && { page: pagination.page }),
        ...(pagination.itemsPerPage !== undefined && {
          itemsPerPage: pagination.itemsPerPage,
        }),
      }
    : {};

  const urlQueryParameters = new URLSearchParams({
    ...queryParams, // Query parameters from the function call
    ...paginationParams, // Pagination parameters to be added to the query
  });

  const fetchRequestUrl =
    url + (urlQueryParameters.toString() ? "?" + urlQueryParameters : "");

  const response = await fetch(fetchRequestUrl, {
    method,
    headers,
    body,
  });

  if (response.status === 401) {
    window.location.href = "/login";
  }

  if (response.status === 204) return null;

  if (!response.ok) {
    throw createFetchError(
      `Request to ${url} failed with status ${response.status}: ${response.statusText}`,
      response.status,
    );
  }

  const responseData = await response.json();

  if (redirectTo) {
    // revalidatePath(redirectTo);
    window.location.href = redirectTo;
  }

  const returnData =
    responseData["@type"] === "Collection" && Array.isArray(responseData.member)
      ? {
          data: responseData.member,
          pagination: getPaginationProperties({ data: responseData }),
        }
      : { data: responseData };

  return returnData;
}
